import React, { useEffect, useRef, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import FormLabel from '@mui/material/FormLabel';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  renderTemplate,
  evaluateHandler,
  parseLeadOutcome,
  buildHeadersObject,
  executeRequest,
} from '../../../../utils/testVendorRequest';
import { Button, Modal } from '@/ui';
import { ErrorAlertList } from '../../../../components/ErrorAlert';
import ProfileSelector from './components/ProfileSelector';
import states from '../../../../utils/states';
import VariablesEditor from './components/VariablesEditor';
import ResponseViewer from './components/ResponseViewer';
import { useTestVendorRequestModalState } from '../../../../hooks/useTestVendorRequestModalState';
import { useTestPostValidation } from '../../../../hooks/useTestPostValidation';
import useHandlerOptions from '../../../../hooks/useHandlerOptions';
import type {
  TestVendorRequestModalProps,
  VendorResponse,
  Handler,
  VariableValues,
} from '../../../../types/testVendorRequest.types';

const LABEL_SX = { display: 'block', mb: 0.5, fontSize: '0.75rem' };

const TestVendorRequestModal: React.FC<TestVendorRequestModalProps> = ({
  isOpen,
  toggle,
  fieldMapping,
  prefix,
  initialValues,
}) => {
  const responseRef = useRef<HTMLDivElement | null>(null);
  const { comboboxOptions: handlerOptions } = useHandlerOptions({ enabled: isOpen });
  const { auth, request, profile, overrides, response, ui, computed } = useTestVendorRequestModalState({
    isOpen,
    fieldMapping,
    prefix,
    initialValues,
  });

  const { basicAuthUsername, basicAuthPassword } = auth;
  const {
    localUrl,
    setLocalUrl,
    localBody,
    setLocalBody,
    localMethod,
    localHeaders,
    updateHeader,
    localHandler,
    setLocalHandler,
  } = request;
  const { selectedProfileId, selectedProfile, handleProfileChange } = profile;
  const { values: overrideValues, setOverrides } = overrides;
  const { lastResponse, setLastResponse, isSending, setIsSending } = response;
  const { isVarsOpen, setIsVarsOpen } = ui;
  const { visibleNames, handlerDisplay, handler, otherStatuses } = computed;

  const { canSend, validationErrors } = useTestPostValidation({
    localUrl,
    localHandler,
    visibleNames,
    overrides: overrideValues,
    isSending,
  });

  const emptyFieldNames = useMemo<string[]>(() => {
    return (visibleNames || []).filter((name) => {
      const value = (overrideValues?.[name] ?? '').trim();
      return value === '';
    });
  }, [visibleNames, overrideValues]);

  const computedPreviewBody = useMemo<string>(() => {
    if (!localMethod || localMethod === 'GET' || !localBody) return '';
    const mergedVars: VariableValues = { ...(selectedProfile || {}), ...(overrideValues || {}) };
    return renderTemplate(localBody, mergedVars);
  }, [localMethod, localBody, selectedProfile, overrideValues]);

  const [manualPreviewBody, setManualPreviewBody] = useState<string | null>(null);

  const onSend = async (): Promise<void> => {
    if (!canSend) return;
    setIsVarsOpen(false);
    setIsSending(true);
    setLastResponse(null);

    const mergedVars: VariableValues = { ...(selectedProfile || {}), ...(overrideValues || {}) };
    const headerLines = localHeaders.filter(Boolean).map((header) => renderTemplate(header, mergedVars));
    const headersObj = buildHeadersObject(headerLines);
    const renderedUrl = renderTemplate(localUrl, mergedVars);
    const finalUrl = renderedUrl.trim();
    const finalBody = localMethod && localMethod !== 'GET' ? renderTemplate(localBody, mergedVars) : undefined;

    try {
      const result = await executeRequest(finalUrl, localMethod || 'POST', headersObj, 30000, finalBody);
      if (!result) {
        throw new Error('No response received');
      }

      const { status, text: vendorRawBody, envelope: vendorRawResponse } = result;
      const { ok, reason: handlerReason } = evaluateHandler(status, vendorRawBody, otherStatuses, handler.code);
      const {
        result: parsedOutcomeResult,
        reason: parsedOutcomeReason,
        leadId: parsedOutcomeLeadId,
      } = parseLeadOutcome(vendorRawBody || vendorRawResponse);

      const responsePayload: VendorResponse = {
        status,
        rawBody:
          typeof vendorRawResponse === 'object'
            ? (vendorRawResponse as { body?: string }).body ?? vendorRawBody
            : vendorRawBody,
        outcome: {
          result: parsedOutcomeResult != null ? parsedOutcomeResult : ok ? 'Success' : 'Fail',
          reason:
            parsedOutcomeReason != null ? parsedOutcomeReason : handlerReason || (ok ? 'OK' : 'Handler returned false'),
          leadId: parsedOutcomeLeadId != null ? parsedOutcomeLeadId : undefined,
        },
      };
      setLastResponse(responsePayload);
    } catch (err) {
      const error = err as { name?: string };
      const isAbort = error?.name === 'AbortError';
      setLastResponse({
        status: 0,
        rawBody: String(err),
        outcome: {
          result: 'Fail',
          reason: isAbort ? 'Request timed out' : String(err),
        },
      });
    } finally {
      setIsSending(false);
    }
  };

  useEffect(() => {
    setManualPreviewBody(null);
  }, [computedPreviewBody]);

  useEffect(() => {
    if (lastResponse && responseRef?.current) {
      try {
        responseRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } catch {}
    }
  }, [lastResponse]);

  const hasEmptyFields = emptyFieldNames.length > 0;
  const shouldValidate = isOpen && (visibleNames?.length ?? 0) > 0;
  const previewBody = manualPreviewBody !== null ? manualPreviewBody : computedPreviewBody;

  const getHandlerSelectValue = (): string => {
    if (!localHandler) return '';
    if (typeof localHandler === 'object') {
      const handlerObj = localHandler as Handler;
      return handlerObj.id?.toString() || handlerObj.value?.toString() || '';
    }
    return localHandler.toString();
  };

  const footerContent = (
    <>
      <ErrorAlertList errors={shouldValidate ? validationErrors : []} />
      <Button color='primary' onClick={onSend} disabled={!canSend || hasEmptyFields} data-testid='send-test-btn'>
        {isSending ? 'Sending...' : 'Send Test Lead'}
      </Button>
      <Button color='secondary' onClick={toggle} data-testid='close-test-modal'>
        Close
      </Button>
    </>
  );

  return (
    <Modal isOpen={isOpen} toggle={toggle} title='Test POST' size='lg' footer={footerContent} testId='test-post-modal'>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, lg: 6 }}>
          <ProfileSelector selectedProfileId={selectedProfileId} onChange={handleProfileChange} />
          <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mt: 1.5, mb: 0.5 }}>
            Reference: Success handler {handlerDisplay}
          </Typography>
          <FormLabel htmlFor='handler-select' sx={LABEL_SX}>
            Select a Handler:
          </FormLabel>
          {/* native so the tests can set these selects by value */}
          <TextField
            select
            id='handler-select'
            slotProps={{ select: { native: true, inputProps: { 'data-testid': 'handler-select' } } }}
            value={getHandlerSelectValue()}
            onChange={(e) => {
              const selectedId = e.target.value;
              if (!selectedId) {
                setLocalHandler(null);
              } else {
                const selectedHandler = handlerOptions.find((h) => h.id.toString() === selectedId);
                setLocalHandler(selectedHandler || { id: parseInt(selectedId, 10), name: '' });
              }
            }}
          >
            <option value=''>Select handler...</option>
            {handlerOptions.map((hOption) => (
              <option key={hOption.id} value={hOption.id.toString()}>
                {hOption.name}
              </option>
            ))}
          </TextField>
          <Divider sx={{ my: 2 }} />
          <Stack direction='row' sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant='subtitle2' component='h6'>
              User variables
            </Typography>
            <Button size='sm' color='link' onClick={() => setIsVarsOpen((v) => !v)} data-testid='vars-toggle'>
              {isVarsOpen ? 'Hide' : 'Show'}
            </Button>
          </Stack>
          <Collapse in={isVarsOpen}>
            <Box sx={{ mt: 2 }}>
              {(visibleNames || []).includes('state') && (
                <Grid container spacing={2} sx={{ alignItems: 'center', mb: 1 }}>
                  <Grid size={{ xs: 12, lg: 4 }}>
                    <FormLabel htmlFor='var-state' title='state'>
                      state
                    </FormLabel>
                  </Grid>
                  <Grid size={{ xs: 12, lg: 8 }}>
                    <TextField
                      select
                      id='var-state'
                      slotProps={{ select: { native: true, inputProps: { 'data-testid': 'var-state' } } }}
                      value={overrideValues.state ?? ''}
                      onChange={(e) => setOverrides('state', e.target.value)}
                    >
                      <option value=''>Select state</option>
                      {(states as string[]).map((abbr) => (
                        <option key={abbr} value={abbr}>
                          {abbr}
                        </option>
                      ))}
                    </TextField>
                  </Grid>
                </Grid>
              )}
              <VariablesEditor
                names={(visibleNames || []).filter((name) => name !== 'state')}
                values={overrideValues}
                setValue={(name, value) => setOverrides(name, value)}
              />
            </Box>
          </Collapse>
        </Grid>
        <Grid size={{ xs: 12, lg: 6 }}>
          <div>
            <FormLabel htmlFor='req-url-input' sx={LABEL_SX}>
              URL
            </FormLabel>
            <TextField
              id='req-url-input'
              type='text'
              value={localUrl}
              onChange={(e) => setLocalUrl(e.target.value)}
              slotProps={{ htmlInput: { 'data-testid': 'request-url-input' } }}
            />
            <FormLabel sx={{ ...LABEL_SX, mt: 2 }}>Headers</FormLabel>
            {([0, 1, 2] as const).map((i) => (
              <TextField
                key={i}
                sx={{ mb: 1 }}
                placeholder={`HTTP Header ${i + 1}`}
                type='text'
                value={localHeaders[i]}
                onChange={(e) => updateHeader(i, e.target.value)}
                slotProps={{ htmlInput: { 'data-testid': `request-header${i + 1}-input` } }}
              />
            ))}

            {localMethod && localMethod !== 'GET' && (
              <>
                <div>
                  <FormLabel htmlFor='req-body-input' sx={{ ...LABEL_SX, mt: 2 }}>
                    Body
                  </FormLabel>
                  <TextField
                    id='req-body-input'
                    multiline
                    minRows={5}
                    value={localBody}
                    onChange={(e) => setLocalBody(e.target.value)}
                    slotProps={{ htmlInput: { 'data-testid': 'request-body-input' } }}
                  />
                </div>
                <div>
                  <FormLabel htmlFor='rendered-req-body-input' sx={{ ...LABEL_SX, mt: 2 }}>
                    Outgoing Request Body (Preview)
                  </FormLabel>
                  <TextField
                    id='rendered-req-body-input'
                    multiline
                    minRows={5}
                    value={previewBody}
                    onChange={(e) => setManualPreviewBody(e.target.value)}
                    slotProps={{ htmlInput: { readOnly: true, 'data-testid': 'rendered-request-body-input' } }}
                  />
                </div>
              </>
            )}
          </div>
        </Grid>
      </Grid>
      <div ref={responseRef} />
      <ResponseViewer response={lastResponse} />
    </Modal>
  );
};

export default TestVendorRequestModal;
